@if (deviceDetail()?.deviceSupportsTopology) {
  <xemex-card-grid>

    <!-- Grid item 1: initial config + sub devices (always shown) -->
    <div class="flex flex-col gap-6">

      @if (deviceDetail()?.protocol == 'DLMS') {
        <xemex-card
          [title]="'devices.detailTabs.configuration.initialConfiguration' | translate"
          [actionLabel]="'general.btnEdit' | translate"
          actionIcon="edit"
          (action)="changeInitialConfigurationDialog()"
        >
          <xemex-detail-field [label]="'devices.detailTabs.configuration.activeProfile' | translate">
            <span
              [class.cursor-pointer]="isXemex() && !!channelConfiguration()?.initialConfiguration?.fileId"
              [class.hover:text-primary]="isXemex() && !!channelConfiguration()?.initialConfiguration?.fileId"
              (click)="isXemex() && openInitialConfigDetails()"
              (keydown.enter)="isXemex() && openInitialConfigDetails()"
            >
              {{ channelConfiguration()?.initialConfiguration?.description ?? '-' }}
            </span>
          </xemex-detail-field>
        </xemex-card>
      }

      @if (isLoadingConfiguration()) {
        <xemex-spinner />
      } @else {
        <xemex-card
          [title]="'devices.detailTabs.configuration.subDevices' | translate"
          [actionLabel]="canAddSubDevice() ? ('general.btnAdd' | translate) : ''"
          actionIcon="add"
          [subtitle]="'devices.detailTabs.configuration.subDevicesSubtitle' | translate"
          (action)="addDeviceConfigurationDialog()"
        >
          @if (channelConfigurationError()) {
            <div class="flex flex-col items-center gap-4 py-6">
              <img src="/assets/icons/refresh_config.svg" alt="Configuration error" />
              <p class="text-xs font-bold">
                {{ getChannelConfigurationErrorMessageKey() | translate }}
              </p>
            </div>
          } @else if (dataSource.data.length === 0) {
            <div class="flex flex-col items-center gap-4 py-6">
              <img src="/assets/icons/refresh_config.svg" alt="No Device Config" />
              <p class="text-xs font-bold">
                {{ 'devices.detailTabs.configuration.noDeviceConfig' | translate }}
              </p>
            </div>
          } @else {
            <div class="flex flex-col divide-y divide-gray-100">
              @for (channel of dataSource.data; track channel.channelNumber) {
                <div
                  class="flex items-center gap-3 py-3 cursor-pointer hover:bg-primary-hover rounded"
                  (click)="isXemex() && openDeviceConfigDetails(channel)"
                  (keydown.enter)="isXemex() && openDeviceConfigDetails(channel)"
                >
                  <div
                    class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    [ngClass]="getCategoryBgClass(channel.category, !!channel.deviceConfiguration)"
                  >
                    <mat-icon
                      [ngClass]="getCategoryIconClass(channel.category, !!channel.deviceConfiguration)"
                    >
                      {{ getCategoryIcon(channel.category) }}
                    </mat-icon>
                  </div>

                  <div class="flex-1 min-w-0">
                    <p
                      class="font-semibold text-sm truncate"
                      [class.text-gray-400]="!channel.deviceConfiguration"
                    >
                      @if (channel.deviceConfiguration) {
                        {{ channel.description }}
                      } @else {
                        {{ 'devices.detailTabs.configuration.notConfigured' | translate }}
                      }
                    </p>
                    <p class="text-xs text-gray-400">
                      {{ channel.category }}
                    </p>
                  </div>

                  <div class="flex items-center">
                    <button
                      mat-icon-button
                      color="primary"
                      type="button"
                      (click)="editDeviceConfigurationDialog(channel); $event.stopPropagation()"
                    >
                      <mat-icon class="text-primary">settings</mat-icon>
                    </button>
                    @if (deviceDetail()?.protocol == 'EnnyLCU') {
                      <button
                        mat-icon-button
                        color="primary"
                        type="button"
                        (click)="deleteDeviceConfigurationDialog(channel); $event.stopPropagation()"
                      >
                        <mat-icon>delete</mat-icon>
                      </button>
                    }
                    <mat-icon class="text-gray-400">chevron_right</mat-icon>
                  </div>
                </div>
              }
            </div>
          }
        </xemex-card>
      }
    </div>

    <!-- Grid item 2: verification history (topology-capable EnnyLCU/DLMS + permission only) -->
    @if (showVerification()) {
      <xemex-card
        [title]="'devices.detailTabs.verification.label' | translate"
        [actionLabel]="'general.btnViewAll' | translate"
        actionIcon="chevron_right"
        [subtitle]="
          isLoadingVerification()
            ? undefined
            : ('devices.detailTabs.verification.showingLast'
              | translate
                : {
                    count: verificationDataSource.data.slice(0, 3).length,
                    total: verifications()?.numberOfItems ?? 0,
                  })
        "
        (action)="viewAllVerifications.emit()"
      >
        @if (isLoadingVerification()) {
          <xemex-spinner />
        } @else {

          <div class="flex flex-col divide-y divide-gray-100">
            @for (v of verificationDataSource.data.slice(0, 3); track v.verificationId) {
              <div
                class="flex items-center gap-3 py-3 cursor-pointer hover:bg-primary-hover rounded"
                (click)="showVerificationDetails(v)"
                (keydown.enter)="showVerificationDetails(v)"
              >
                <div
                  class="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0"
                >
                  <mat-icon class="text-primary !w-5 !h-5 !text-[20px]">manage_search</mat-icon>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="font-semibold text-sm">
                    {{ v.initiatedOn | date: 'dd MMM yyyy, HH:mm' }}
                  </p>
                  <p class="text-xs text-gray-400">{{ v.initiatedBy }}</p>
                </div>
                <div class="flex items-center gap-1">
                  <xemex-status-chip
                    [status]="getVerificationChipStatus(v.status)"
                    [label]="v.status"
                  />
                  <mat-icon class="text-gray-400 !w-4 !h-4 !text-[16px]">chevron_right</mat-icon>
                </div>
              </div>
            }

            @if (verificationDataSource.data.length === 0) {
              <div class="flex flex-col items-center gap-4 py-4">
                <img src="/assets/icons/refresh_config.svg" alt="No Verifications" />
                <p class="text-xs font-bold">
                  {{ 'devices.detailTabs.verification.noVerifications' | translate }}
                </p>
              </div>
            }
          </div>

          <div class="mt-4 flex justify-end">
            <button
              mat-flat-button
              color="primary"
              type="button"
              (click)="performVerification()"
              [disabled]="isPerformingVerification"
            >
              @if (isPerformingVerification) {
                <mat-spinner diameter="20" color="accent"></mat-spinner>
              } @else {
                <mat-icon>play_arrow</mat-icon>
              }
              {{ 'devices.detailTabs.verification.startVerification' | translate }}
            </button>
          </div>
        }
      </xemex-card>
    }

  </xemex-card-grid>
} @else {
  <div class="flex flex-col items-center gap-4 mt-5">
    <img src="assets/images/empty/document.svg" alt="empty" />
    <div class="text-xs font-bold">
      {{ 'devices.detailTabs.configuration.notSupported' | translate }}
    </div>
  </div>
}
